"use client";

import { PlayIcon } from "@heroicons/react/24/solid";

/*
 * "See how it works" under the hero copy: it opens the landing's Videos
 * window on the why-us film, the same way the nav opens Pricing or Contact.
 * The window and the film load only when the visitor reaches for the button,
 * so the landing's first load and its static, cacheable page stay as they are.
 */
export const SEE_HOW_IT_WORKS = "See how it works";

/** What the button does: open the Videos window, and warm it on intent. */
export type LandingWatchAction = {
  onClick: () => void;
  onIntent: () => void;
};

/**
 * "desktop" is the nav's weight, "mobile" a phone's touch size, and "disc" the
 * play disc alone, labelled for assistive tech, for a row with no room for the words.
 */
export type WatchVideoButtonVariant = "desktop" | "mobile" | "disc";

const SIZES: Record<WatchVideoButtonVariant, string> = {
  desktop: "h-[46px] px-4 text-[14px]",
  mobile: "h-[54px] px-5 text-[16px]",
  disc: "size-[54px] justify-center",
};

export default function WatchVideoButton({
  t,
  variant,
  watch,
}: {
  t: (source: string) => string;
  variant: WatchVideoButtonVariant;
  watch: LandingWatchAction;
}) {
  const disc = variant === "disc";
  return (
    <button
      type="button"
      aria-label={disc ? t(SEE_HOW_IT_WORKS) : undefined}
      title={disc ? t(SEE_HOW_IT_WORKS) : undefined}
      data-landing-watch="true"
      onClick={watch.onClick}
      // Warm the window and its film on intent, so both are usually ready by the click.
      onPointerEnter={watch.onIntent}
      onFocus={watch.onIntent}
      className={`inline-flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-full font-medium tracking-[0.01em] text-white/80 transition-colors duration-200 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-200 motion-reduce:transition-none ${SIZES[variant]}`}
    >
      <span aria-hidden="true" className={`grid place-items-center rounded-full bg-white/[0.08] ring-1 ring-white/15 ${disc ? "size-10" : "size-7"}`}>
        <PlayIcon className={`translate-x-px rtl:-scale-x-100 ${disc ? "size-4" : "size-3.5"}`} />
      </span>
      {disc ? null : t(SEE_HOW_IT_WORKS)}
    </button>
  );
}
